import React, {useEffect, useState} from 'react';
import {
    FAQLink,
    FormSubmissionError, RequiredAstrix,
    RequiredFieldMessage,
    TermsConditionsLink
} from "../components/FormElements";
import {Formik} from "formik";
import * as yup from "yup";
import {
    EmailValidation,
    FirstNameValidation,
    LastNameValidation,
    MobileValidation,
    TermsAndConditionsValidation,
    RecaptchaValidation
} from "../components/FormValidations";
import InputField from "../components/InputField";
import CheckboxField from "../components/CheckboxField";
import TextareaField from "../components/TextareaField";
import progressOption from "../../progress-bar";
import handleFormSubmit, {netballGrantEndpoint} from "../client-api/ANZAPI";
import RecapthaField from "../components/RecapthaField";
import {SuccessMessage} from "../components/Messages";
import SelectField from "../components/SelectField";
import NumberField from "../components/NumberField";
import {updateLinkTrackingFields} from "../components/LinkTracking";
import {removePageClassContent} from "../../script";

const FIRST_NAME_FIELD = 'FirstName';
const LAST_NAME_FIELD = 'LastName';
const EMAIL_FIELD = 'Email';
const MOBILE_FIELD = 'Mobile';
const TEAM_NAME_FIELD = 'TeamName';
const ADDRESS_FIELD = 'Address';
const SUBURB_FIELD = 'Suburb';
const POSTCODE_FIELD = 'Postcode';
const CITY_FIELD = 'City';
const REGION_FIELD = 'Region';
const REQUEST_MESSAGE_FIELD = 'RequestMessage';
const FIND_PROMOTION_FIELD = 'FindPromotion';
const AGREE_TC_FIELD = 'AgreeTC';
const SIGNUP_FIELD = 'SignUp';
const RECAPTCHA_FIELD = 'Recaptcha';


const initialValues = {
    [FIRST_NAME_FIELD]: '',
    [LAST_NAME_FIELD]: '',
    [EMAIL_FIELD]: '',
    [MOBILE_FIELD]: '',
    [TEAM_NAME_FIELD]: '',
    [ADDRESS_FIELD]: '',
    [SUBURB_FIELD]: '',
    [POSTCODE_FIELD]: '',
    [CITY_FIELD]: '',
    [REGION_FIELD]: '',
    [REQUEST_MESSAGE_FIELD]: '',
    [FIND_PROMOTION_FIELD]: '',
    [AGREE_TC_FIELD]: false,
    [SIGNUP_FIELD]: false,
    [RECAPTCHA_FIELD]: ''
}

const submissionValidation = yup.object().shape({
    [FIRST_NAME_FIELD]: FirstNameValidation,
    [LAST_NAME_FIELD]: LastNameValidation,
    [EMAIL_FIELD]: EmailValidation,
    [MOBILE_FIELD]: MobileValidation,
    [TEAM_NAME_FIELD]: yup.string().required('Club/Team Name is required.'),
    [ADDRESS_FIELD]: yup.string().required('Address is required'),
    [SUBURB_FIELD]: yup.string().required('Suburb is required'),
    [CITY_FIELD]: yup.string().required('City is required'),
    [REGION_FIELD]: yup.string().required('Region is required'),
    [FIND_PROMOTION_FIELD]: yup.string().required('Promotion is required'),
    [AGREE_TC_FIELD]: TermsAndConditionsValidation,
    [RECAPTCHA_FIELD]: RecaptchaValidation,
})

/**
 * Form has got two sections before it is completed. This is used to identify if the user has completed the required
 * fields before buttons become enabled.
 * @type {{first: (string)[], second: (string)[]}}
 */
const FormSectionValidation = {
    first: [
        FIRST_NAME_FIELD, LAST_NAME_FIELD, EMAIL_FIELD, MOBILE_FIELD, TEAM_NAME_FIELD,
        ADDRESS_FIELD, SUBURB_FIELD, CITY_FIELD, REGION_FIELD,
    ],
    second: [FIND_PROMOTION_FIELD, AGREE_TC_FIELD, RECAPTCHA_FIELD]
}


const regionOptions = [
    {label: 'Northland'},
    {label: 'Auckland'},
    {label: 'Waikato'},
    {label: 'Bay of Plenty'},
    {label: 'Gisborne/Hawkes Bay'},
    {label: 'Taranaki'},
    {label: 'Manawatu/Wanganui'},
    {label: 'Wellington'},
    {label: 'Marlborough/Nelson'},
    {label: 'West Coast'},
    {label: 'Canterbury'},
    {label: 'Otago'},
    {label: 'Southland'},
]

const promotionOptions = [
    {label: 'ANZ'},
    {label: 'Club or School'},
    {label: 'Social media'},
    {label: 'I applied last year'},
    {label: 'Internet'},
    {label: 'Newspaper or Magazine'},
    {label: 'Radio'},
    {label: 'Word of mouth'},
    {label: 'Other'},
]


const applicationDetailStageTracking = {
    pageName: 'anzcourtside:grant-scheme:apply:p2:applicationdetails',
    formName: 'anz-courtside:grant-scheme',
    formStatus: 'in-progress',
    journeyName: 'registration:netball_grant_scheme',
    journeyStepName: 'application_details',
    journeyStatus: 'in-progress'
}

const confirmationLinkTracking = {
    pageName: 'anzcourtside:grant-scheme:apply:p3:confirmation',
    formName: 'anz-courtside:grant-scheme',
    formStatus: 'submitted',
    journeyName: 'registration:netball_grant_scheme',
    journeyStepName: 'confirmation',
    journeyStatus: 'complete'
}


/**
 * Checks the validation of the first form.
 *
 * @param errors form errors
 * @returns {boolean}
 */
const getFirstFormValidation = (errors) => {
    const firstValidation = FormSectionValidation.first;
    const formErrors = Object.keys(errors);

    return firstValidation.some((item) => formErrors.includes(item))
}

/**
 * Checks the validation of the second form
 *
 * @param errors
 * @returns {boolean}
 */
const getSecondFormValidation = (errors) => {
    const secondValidation = FormSectionValidation.second;
    const formErrors = Object.keys(errors);

    return secondValidation.some((item) => formErrors.includes(item))
}

const NetballGrantForm = (props) => {
    const [formStage, setFormStage] = useState(1)
    const [submissionError, setSubmissionError] = useState(null);
    const [successSubmitted, setSuccessSubmitted] = useState(false);

    useEffect(() => {
        progressOption(formStage)
    }, [formStage])

    return (
        <>
            {successSubmitted === true ? (
                <SuccessMessage
                    heading={'Nice Work'}
                    message={'Your application has been submitted.'}
                    extraMessage={<>See the <a href={'#'} data-toggle="modal" data-target="#JudgingNetballGrant"
                        className={'modal-link'}>FAQs</a> for more information.</>}
                />
            ) : (
                <>
                    {formStage === 2 && <h2>Application details</h2>}
                    <RequiredFieldMessage/>
                    {formStage === 1 && <hr/>}
                    <Formik
                        initialValues={{...initialValues}}
                        initialErrors={{...initialValues}}
                        onSubmit={(values, {setSubmitting, resetForm}) => {
                            handleFormSubmit(values, netballGrantEndpoint(props.elementId))
                                .then((res) => {
                                    setSubmitting(false)
                                    if (res.success) {
                                        setFormStage(3)
                                        resetForm({...initialValues});
                                        setSuccessSubmitted(true);

                                        removePageClassContent('.netball-grant-content')
                                        updateLinkTrackingFields(confirmationLinkTracking)
                                        pageView();

                                        return;
                                    }

                                    setSubmissionError(res?.message)
                                    $('html, body').animate({
                                        'scrollTop': document.getElementById("submissionErrorId").offsetTop - 50
                                    }, 500);
                                })
                        }}
                        validationSchema={submissionValidation}
                    >{(formikProps) => (
                        <>
                            {submissionError !== null && (
                                <div id={'submissionErrorId'}>
                                    <FormSubmissionError message={submissionError}/>
                                </div>
                            )}

                            <form onSubmit={formikProps.handleSubmit}>
                                <div className={`form-group ${formStage === 1 ? 'd-block' : 'd-none'}`}>
                                    <h3>Applicant details</h3>
                                    <InputField
                                        id={'firstName'}
                                        label={'First name'}
                                        name={FIRST_NAME_FIELD}
                                        required={true}
                                        maxLength={30}
                                        {...formikProps}
                                    />

                                    <InputField
                                        id={'lastName'}
                                        label={'Last name'}
                                        name={LAST_NAME_FIELD}
                                        maxLength={30}
                                        required={true}
                                        {...formikProps}
                                    />

                                    <InputField
                                        id={'email'}
                                        label={'Email'}
                                        type={'email'}
                                        name={EMAIL_FIELD}
                                        maxLength={129}
                                        required={true}
                                        {...formikProps}
                                    />

                                    <hr/>

                                    <h3>Applicant mobile <RequiredAstrix/></h3>
                                    <NumberField name={MOBILE_FIELD} id={'mobile'} allowNegative={false} maxLength={20}
                                                 className={'short'}/>
                                    <hr/>

                                    <h3>Application name</h3>
                                    <InputField
                                        id={'club'}
                                        label={'Club/Team Name'}
                                        fieldLength={'full w-100'}
                                        name={TEAM_NAME_FIELD}
                                        required={true}
                                        maxLength={100}
                                        {...formikProps}
                                    />

                                    <InputField
                                        id={'address'}
                                        label={'Address'}
                                        name={ADDRESS_FIELD}
                                        required={true}
                                        maxLength={200}
                                        {...formikProps}
                                    />

                                    <div className={'row'}>
                                        <div className={'col-12 col-md-6 pr-md-0'}>
                                            <InputField
                                                id={'suburb'}
                                                label={'Suburb'}
                                                fieldLength={'full'}
                                                name={SUBURB_FIELD}
                                                required={true}
                                                maxLength={60}
                                                classNameWrapper={'w-100'}
                                                {...formikProps}
                                            />
                                        </div>

                                        <div className={'col-12 col-md-6'}>
                                            <InputField
                                                id={'postcode'}
                                                label={'Postcode'}
                                                fieldLength={'short'}
                                                name={POSTCODE_FIELD}
                                                maxLength={4}
                                                classNameWrapper={'w-100'}
                                                {...formikProps}
                                            />
                                        </div>
                                    </div>

                                    <div className={'row'}>
                                        <div className={'col-12 col-md-6 pr-md-0'}>
                                            <InputField
                                                id={'city'}
                                                label={'City'}
                                                fieldLength={'full'}
                                                name={CITY_FIELD}
                                                required={true}
                                                maxLength={60}
                                                {...formikProps}
                                            />
                                        </div>

                                        <div className={'col-12 col-md-6'}>
                                            <SelectField
                                                id={'region'}
                                                name={REGION_FIELD}
                                                label={'Region'}
                                                options={regionOptions}
                                                required={true}
                                                {...formikProps}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div id='stage2' className={`form-group ${formStage === 2 ? 'd-block' : 'd-none'}`}>
                                    <label>Application Title <RequiredAstrix/></label>
                                    <h3 className={'pb-3'}>{formikProps.values[TEAM_NAME_FIELD]}</h3>
                                    <label>
                                        In this section, please let us know the impact an ANZ Netball Grant would make to your team. Include the age/year group of your players. Please liaise with your team manager to ensure one application is submitted per team.<RequiredAstrix/>
                                    </label>
                                    <TextareaField
                                        label={<>Checkout the <a href={'#'} data-toggle="modal"
                                                                 data-target="#JudgingNetballGrant">FAQs</a> for more information</>}
                                        name={REQUEST_MESSAGE_FIELD}
                                        id={'requestMessage'}
                                        fieldLength={'full'}
                                        placeholder={'3000 characters or 500 words'}
                                        maxLength={3000}
                                        {...formikProps}
                                    />
                                    <hr/>

                                    <SelectField
                                        id={'promotion'}
                                        name={FIND_PROMOTION_FIELD}
                                        label={'How did you find out about this promotion?'}
                                        options={promotionOptions}
                                        required={true}
                                        extraClass={'pb-4 mt-0'}
                                        selectClass={'short'}
                                        {...formikProps}
                                    />

                                    <div className={'form-group mt-0'}>
                                        <CheckboxField
                                            name={AGREE_TC_FIELD}
                                            value={formikProps.values[AGREE_TC_FIELD]}
                                            required={true}
                                            label={<>I agree to the <a href={"#"} data-toggle="modal"
                                                                       data-target="#TCNetballGrant">Terms
                                                and Conditions</a></>}
                                        />
                                    </div>

                                    <div className={'form-group mt-0'}>
                                        <CheckboxField
                                            name={SIGNUP_FIELD}
                                            value={formikProps.values[SIGNUP_FIELD]}
                                            label={'Tick here to sign up to receive news, updates and exclusive offers from ANZ Courtside'}
                                        />
                                    </div>
                                </div>

                                <hr/>

                                {formStage === 1 ? (
                                    <div className={'links pt-2'}>
                                        <button
                                            className={'btn btn-primary mr-5'}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (getFirstFormValidation(formikProps.errors)) {
                                                    return;
                                                }
                                                removePageClassContent('.netball-grant-content');
                                                setFormStage(2)
                                                updateLinkTrackingFields(applicationDetailStageTracking)
                                                pageView();

                                                $('html, body').animate({
                                                    'scrollTop': document.getElementById("stage2").offsetTop
                                                }, 1000);
                                            }}
                                            disabled={getFirstFormValidation(formikProps.errors)}
                                        >Next
                                        </button>
                                        <FAQLink target={'JudgingNetballGrant'} title={'FAQs'} />
                                        <TermsConditionsLink target={'TCNetballGrant'} />
                                    </div>
                                ) : (
                                    <div>
                                        <div className={'pt-4'}>
                                            <RecapthaField name={RECAPTCHA_FIELD}/>
                                        </div>
                                        <hr/>
                                        <div className={'links'}>
                                            <button
                                                type="submit"
                                                className={'btn btn-primary mr-5'}
                                                disabled={getSecondFormValidation(formikProps.errors) || formikProps.isSubmitting}
                                            >Submit application
                                            </button>
                                            <FAQLink target={'JudgingNetballGrant'} title={'FAQs'} />
                                            <TermsConditionsLink target={'TCNetballGrant'} />
                                        </div>
                                    </div>
                                )}
                            </form>
                        </>
                    )}
                    </Formik>
                </>
            )}
        </>
    )
}

export default NetballGrantForm;
